'use client';

import { useAutoAnimate } from '@formkit/auto-animate/react';
import { cn } from 'cn';
import {
  type ComponentProps,
  type ComponentType,
  type ReactNode,
  useMemo,
  useState,
} from 'react';
import { FormRow, Icon } from '~/atoms';
import {
  Button,
  EmptyState,
  FieldErrorDisplay,
  InputLabel,
  SearchInput,
  VirtualizedList,
} from '~/molecules';

import { isFieldRequired } from '@vestigas/utils';

import { ValidationError } from '../ValidationError';

import { ListSortButtons } from './ListSortButtons';

const defaultAddButtonText = 'Neues Element';
const defaultEmptyStateText = 'Du hast keine Elemente hinzugefügt.';
const defaultSearchPlaceholderText = 'Suche in Liste';

type ListItem = {
  context?: Record<string, unknown>;
  field: any;
  form: any;
  index: number;
  length?: number;
  namePrefix?: string;
};

type P = {
  readonly addButtonText?: string;
  readonly additionalActions?: ReactNode;
  readonly appendNewItems?: boolean;
  readonly canAdd?: boolean;
  readonly canDelete?: boolean | ((item: unknown, index: number) => boolean);
  readonly canSearch?: boolean;
  readonly canSort?: boolean;
  readonly context?: Record<string, unknown>;
  readonly deleteButtonClassName?: string;
  readonly deleteButtonTitle?: string;
  readonly EmptyStateIllustration?: ReactNode;
  readonly emptyStateText?: string | string[] | undefined;
  readonly errorDisplay?: FieldErrorDisplay;
  readonly field: any;
  readonly form: any;
  readonly isDisabled?: boolean;
  readonly isMultiLineForm?: boolean;
  readonly itemActionsClassName?: string;
  readonly label?: string;
  readonly listClassName?: string;
  readonly ListItemComponent: ComponentType<ListItem>;
  readonly manyItemsThreshold?: number;
  readonly namePrefix?: string;
  // `field`/`form` are untyped throughout this component (see `ListItem`
  // above), so the factory variant takes `any[]` rather than `unknown[]`:
  // ListManager isn't generic over the item type, and `unknown[]` would
  // reject any caller whose factory expects a concrete item shape.
  readonly newItemTemplate:
    | Record<string, unknown>
    | ((items: any[]) => Record<string, unknown>);
  readonly searchPlaceholderText?: string;
  readonly sortButtonsClassName?: string;
  readonly virtualizedListProps?: Omit<
    ComponentProps<typeof VirtualizedList>,
    'items' | 'renderItem'
  >;
} & ComponentStyling;

/**
 * Component that renders a list manager component that allows adding, deleting, and reordering a list of form elements
 * inside a form managed with TanStack Form.
 *
 * @param addButtonText - The text to display on the add button.
 * @param additionalActions - Additional actions to display in the list manager.
 * @param appendNewItems - Whether to append new items to the end of the list or prepend them to the beginning.
 * @param canAdd - Whether the add button should be displayed.
 * @param canDelete - Whether the delete buttons should be displayed for each list item. Can be a boolean or a function that receives the item and index and returns a boolean for per-item control (e.g. only allow deleting unsaved items).
 * @param canSearch - Whether the user can filter the list items with a search input.
 * @param canSort - Whether the sort buttons should be displayed for each list item.
 * @param className - Additional CSS class names for the component.
 * @param context - Additional context data to pass to list items.
 * @param deleteButtonClassName - Additional CSS class names for the delete buttons.
 * @param deleteButtonTitle - The title to display on the delete button.
 * @param emptyStateText - The text to display when the list is empty.
 * @param EmptyStateIllustration - The illustration to display when the list is empty.
 * @param field - The field object from TanStack Form containing state and methods.
 * @param form - The form object from TanStack Form.
 * @param isDisabled - Whether the list manager interaction buttons (add, delete, sort) are disabled.
 * @param isMultiLineForm - Whether the form is multi-line.
 * @param itemActionsClassName - Additional CSS class names for the item actions container.
 * @param listClassName - Additional CSS class names for the list container.
 * @param ListItemComponent - The component to render for each list item.
 * @param manyItemsThreshold - The number of items at which a second add button should be displayed below the list.
 * @param namePrefix - The prefix to add to the field names.
 * @param newItemTemplate - The template for new items, or a factory called with the list's current, live value (so a default that depends on existing items, e.g. "first unused option", is never stale).
 * @param searchPlaceholderText - The placeholder text for the search input.
 * @param sortButtonsClassName - Additional CSS class names for the sort buttons.
 * @param style - Additional inline styles for the component.
 */
export const ListManager = ({
  addButtonText = defaultAddButtonText,
  additionalActions,
  appendNewItems = false,
  canAdd = true,
  canDelete = true,
  canSearch = false,
  canSort = false,
  className,
  context = {},
  deleteButtonClassName,
  deleteButtonTitle,
  EmptyStateIllustration,
  emptyStateText,
  errorDisplay = 'overlay',
  field,
  form,
  isDisabled = false,
  isMultiLineForm,
  itemActionsClassName,
  label,
  listClassName,
  ListItemComponent,
  manyItemsThreshold = 3,
  namePrefix = '',
  newItemTemplate,
  searchPlaceholderText = defaultSearchPlaceholderText,
  sortButtonsClassName,
  style,
  virtualizedListProps,
}: P) => {
  const [animationParent] = useAutoAnimate();

  const { insertValue, moveValue, pushValue, removeValue, store } = field;
  const { errors } = store.state.meta;

  const isRequired = isFieldRequired(
    field.options.form.options.validators,
    field.name,
  );

  const hasErrors = Boolean(errors?.length);
  const errorMessage = errors
    ?.map(({ message }: { message: string }) => message)
    .join(', ');

  const emptyState =
    emptyStateText === null || emptyStateText === ''
      ? null
      : (emptyStateText ?? defaultEmptyStateText);

  const fieldsCount = field.state.value?.length ?? 0;
  const hasMultipleItems = fieldsCount > 1;
  const hasManyItems = fieldsCount >= manyItemsThreshold;

  const handleAddItem = () => {
    // Read the live store value rather than the `field` prop's own state:
    // a factory template needs the current items at click time, and this
    // field may be in `mode="array"`, which doesn't re-render on changes to
    // individual item values, only on additions/removals.
    const template =
      typeof newItemTemplate === 'function'
        ? newItemTemplate(field.store.state.value ?? [])
        : newItemTemplate;

    if (appendNewItems) {
      pushValue(template);
    } else {
      insertValue(0, template);
    }
  };

  const handleDeleteItem = (index: number) => {
    removeValue(index);
  };

  const handleMoveItem = (from: number, to: number) => {
    moveValue(from, to);
  };

  /** Merge isDisabled into context so ListItemComponents can disable their fields. */
  const itemContext = useMemo(
    () => ({ ...context, isDisabled }),
    [context, isDisabled],
  );

  const [searchString, setSearchString] = useState('');

  const { items, hiddenItemsMap } = useMemo(() => {
    const values = field.store.state.value ?? [];

    if (values.length === 0) {
      return {
        items: [],
        hiddenItemsMap: {},
      };
    }

    if (searchString.length > 2) {
      const needle = searchString.toLowerCase();
      const hiddenItemsMap: Record<number, boolean> = {};

      for (const [index, item] of values.entries()) {
        const haystack = Object.values(item)
          .map((value) =>
            typeof value === 'object' ? undefined : String(value).toLowerCase(),
          )
          .join(';');

        hiddenItemsMap[index] = !haystack.includes(needle);
      }

      return {
        items: values,
        hiddenItemsMap,
      };
    }

    return {
      items: values,
      hiddenItemsMap: {},
    };
  }, [field.store.state.value, searchString]);

  const hasNoMatchingItems =
    searchString.length > 2 &&
    items.every((_: unknown, index: number) => hiddenItemsMap[index]);

  const canDeleteItem = (index: number) =>
    typeof canDelete === 'function'
      ? canDelete(items[index] as unknown, index)
      : (canDelete ?? true);

  return (
    <div className={cn('flex flex-1 flex-col gap-4', className)} style={style}>
      {Boolean(label) && (
        <InputLabel className="-mb-3.5" isRequired={isRequired}>
          {label}
        </InputLabel>
      )}
      <div className="flex items-center gap-4">
        {canAdd && (
          <Button
            buttonStyle="secondary"
            disabled={isDisabled}
            icon={<Icon metaphor="add" />}
            text={addButtonText}
            type="button"
            onMouseDown={handleAddItem}
          />
        )}
        {additionalActions}
        {canSearch && hasManyItems ? (
          <div className="flex-1">
            <SearchInput
              placeholder={searchPlaceholderText}
              value={searchString}
              onChange={setSearchString}
            />
          </div>
        ) : null}
      </div>
      {virtualizedListProps ? (
        <VirtualizedList
          items={items}
          renderItem={(_: unknown, index: number) => {
            const np = [namePrefix, `${field.name}[${index}]`].join('');

            return (
              <FormRow
                key={`item-${index}`}
                className={cn('flex-nowrap pb-4', {
                  hidden: hiddenItemsMap[index],
                })}
                vAlign="top"
              >
                <ListItemComponent
                  context={itemContext}
                  field={field}
                  form={form}
                  index={index}
                  length={fieldsCount}
                  namePrefix={np}
                />
                {(canSort && hasMultipleItems) || canDeleteItem(index) ? (
                  <div
                    className={cn(
                      'flex items-start gap-2',
                      {
                        'items-center': canSort && !isMultiLineForm,
                      },
                      itemActionsClassName,
                    )}
                  >
                    {canSort && hasMultipleItems ? (
                      <ListSortButtons
                        className={cn('text-right', sortButtonsClassName)}
                        index={index}
                        isDisabled={isDisabled}
                        length={fieldsCount}
                        move={handleMoveItem}
                      />
                    ) : null}
                    {canDeleteItem(index) ? (
                      <Button
                        aria-label={deleteButtonTitle}
                        buttonStyle="secondary"
                        className={cn('px-2', deleteButtonClassName)}
                        disabled={isDisabled}
                        icon={<Icon metaphor="delete" />}
                        size="small"
                        title={deleteButtonTitle}
                        onMouseDown={() => {
                          handleDeleteItem(index);
                        }}
                      />
                    ) : null}
                  </div>
                ) : null}
              </FormRow>
            );
          }}
          {...virtualizedListProps}
        />
      ) : (
        <div
          ref={animationParent}
          className={cn('flex w-full flex-col gap-4', listClassName)}
        >
          {items.map((_: unknown, index: number) => {
            const np = [namePrefix, `${field.name}[${index}]`].join('');

            return (
              <FormRow
                key={`item-${index}`}
                className={cn('flex-nowrap', {
                  hidden: hiddenItemsMap[index],
                })}
                vAlign="top"
              >
                <ListItemComponent
                  context={itemContext}
                  field={field}
                  form={form}
                  index={index}
                  length={fieldsCount}
                  namePrefix={np}
                />
                {(canSort && hasMultipleItems) || canDeleteItem(index) ? (
                  <div
                    className={cn(
                      'flex items-start gap-2',
                      {
                        'items-center': canSort && !isMultiLineForm,
                      },
                      itemActionsClassName,
                    )}
                  >
                    {canSort && hasMultipleItems ? (
                      <ListSortButtons
                        className={cn('text-right', sortButtonsClassName)}
                        index={index}
                        isDisabled={isDisabled}
                        length={fieldsCount}
                        move={handleMoveItem}
                      />
                    ) : null}
                    {canDeleteItem(index) ? (
                      <Button
                        aria-label={deleteButtonTitle}
                        buttonStyle="secondary"
                        className={cn('px-2', deleteButtonClassName)}
                        disabled={isDisabled}
                        icon={<Icon metaphor="delete" />}
                        size="small"
                        title={deleteButtonTitle}
                        onMouseDown={() => {
                          handleDeleteItem(index);
                        }}
                      />
                    ) : null}
                  </div>
                ) : null}
              </FormRow>
            );
          })}
        </div>
      )}
      {hasNoMatchingItems ? (
        <EmptyState
          Illustration={null}
          hint={`Kein Element der Liste enthält "${searchString}"`}
        />
      ) : null}
      {canAdd && hasManyItems ? (
        <div>
          <Button
            buttonStyle="secondary"
            disabled={isDisabled}
            icon={<Icon metaphor="add" />}
            text={addButtonText}
            type="button"
            onMouseDown={handleAddItem}
          />
        </div>
      ) : null}

      {!fieldsCount && emptyState ? (
        <EmptyState Illustration={EmptyStateIllustration} hint={emptyState} />
      ) : null}

      {errorDisplay === 'block' ? (
        <div className="min-h-5">
          {hasErrors ? (
            <ValidationError className="static block translate-y-0 bg-transparent leading-tight">
              {errorMessage}
            </ValidationError>
          ) : null}
        </div>
      ) : null}
      {errorDisplay === 'push' && hasErrors ? (
        <div>
          <ValidationError className="static block translate-y-0 bg-transparent leading-tight">
            {errorMessage}
          </ValidationError>
        </div>
      ) : null}
      {errorDisplay === 'overlay' && hasErrors ? (
        <span>
          <ValidationError>{errorMessage}</ValidationError>
        </span>
      ) : null}
    </div>
  );
};
